import type { ShippingLabelViewModel } from "@/modules/chat-tools/domain/build-shipping-label";
import {
  buildContentModeCommand,
  contentModeRasterThreshold,
  type LuckContentMode,
} from "./content-modes";
import { imageDataToLuckRaster } from "@/modules/chat-tools/presentation/lib/bitmap-to-luck-raster";
import { renderLabelBitmap } from "@/modules/chat-tools/presentation/lib/render-label-bitmap";
import { renderLabelBitmapTextMode } from "@/modules/chat-tools/presentation/lib/render-label-text-mode";
import { LuckPrinterBleTransport } from "./ble-transport";
import type { DeviceProfile } from "./device-profiles";
import {
  buildPostRasterCommands,
  buildPreRasterCommands,
  type LuckPaperMode,
} from "./print-modes";
import {
  buildSetDensityCommand,
  buildStopJobCommand,
  sleep,
  type LuckPrinterDensity,
} from "./protocol";
import { buildGsV0RasterPacket } from "./raster";
import {
  getActiveLuckPrinterProfile,
  getSavedLuckPrinterContentMode,
  getSavedLuckPrinterDensity,
  getSavedLuckPrinterFeedDots,
  getSavedLuckPrinterPaperMode,
  saveLuckPrinterContentMode,
  saveLuckPrinterDensity,
  saveLuckPrinterPaperMode,
} from "./storage";

export type PrintShippingLabelOptions = {
  element?: HTMLElement | null;
  label?: ShippingLabelViewModel;
  density?: LuckPrinterDensity;
  feedDots?: number;
  contentMode?: LuckContentMode;
  paperMode?: LuckPaperMode;
  profile?: DeviceProfile;
  transport?: LuckPrinterBleTransport;
  onProgress?: (message: string) => void;
};

async function renderShippingLabelBitmap(
  options: PrintShippingLabelOptions,
  profile: DeviceProfile,
  contentMode: LuckContentMode,
): Promise<ImageData> {
  if (contentMode === "text") {
    if (!options.label) {
      throw new Error("Text mode requires label data");
    }
    return renderLabelBitmapTextMode(options.label, profile.printWidthPx);
  }

  if (options.element) {
    return renderLabelBitmap(options.element, profile.printWidthPx);
  }

  if (options.label) {
    const { mountShippingLabelElement } = await import(
      "@/modules/chat-tools/presentation/lib/render-shipping-label-element"
    );
    const mounted = await mountShippingLabelElement(options.label);
    try {
      return await renderLabelBitmap(mounted.element, profile.printWidthPx);
    } finally {
      mounted.unmount();
    }
  }

  throw new Error("Label element or data is required");
}

export async function printShippingLabelViaBle(
  options: PrintShippingLabelOptions,
): Promise<void> {
  const transport = options.transport ?? new LuckPrinterBleTransport();
  const ownsTransport = !options.transport;
  const profile = options.profile ?? getActiveLuckPrinterProfile();
  const contentMode = options.contentMode ?? getSavedLuckPrinterContentMode();
  const paperMode = options.paperMode ?? getSavedLuckPrinterPaperMode();
  const density = options.density ?? getSavedLuckPrinterDensity();
  const feedDots = options.feedDots ?? getSavedLuckPrinterFeedDots();

  try {
    if (!transport.connectedDevice) {
      options.onProgress?.("ກຳລັງເຊື່ອມຕໍ່...");
      await transport.connect();
    }

    options.onProgress?.(
      contentMode === "image" ? "ກຳລັງກະກຽມຮູບ..." : "ກຳລັງກະກຽມຂໍ້ຄວາມ...",
    );
    const imageData = await renderShippingLabelBitmap(
      options,
      profile,
      contentMode,
    );
    const raster = imageDataToLuckRaster(
      imageData,
      contentModeRasterThreshold(contentMode),
    );
    const rasterPacket = buildGsV0RasterPacket(
      raster,
      imageData.width,
      imageData.height,
    );

    const prePrintCommands = [
      ...profile.enableSequence,
      buildSetDensityCommand(density),
      buildContentModeCommand(contentMode),
      ...buildPreRasterCommands(paperMode),
    ];

    options.onProgress?.("ກຳລັງສົ່ງໄປເຄື່ອງພິມ...");
    for (const command of prePrintCommands) {
      await transport.write(command);
      await sleep(profile.chunkDelayMs);
    }

    await transport.writeChunks(
      rasterPacket,
      profile.chunkSize,
      profile.chunkDelayMs,
    );

    for (const command of buildPostRasterCommands(paperMode, feedDots)) {
      await transport.write(command);
      await sleep(profile.chunkDelayMs);
    }

    await transport.write(buildStopJobCommand());

    options.onProgress?.("ກຳລັງລໍຖ້າເຄື່ອງພິມ...");
    await transport.waitForStopAck();
    saveLuckPrinterDensity(density);
    saveLuckPrinterContentMode(contentMode);
    saveLuckPrinterPaperMode(paperMode);
  } finally {
    if (ownsTransport) {
      await transport.disconnect();
    }
  }
}
